import './styles.scss';
import React, {Dispatch, useCallback, useEffect, useState} from "react";

/* Types, Constants, Utils */
import Utils from "../../../utils";
import {RecordsContext} from "../../../types/document";
import * as Constants from "../../../constants";
import {ISearchDate, ISearchEntry, ISearchState} from "../../../types/redux/search";

/* Bootstrap */
import {Button, Card, Col,  Container, Nav, Row, Tab} from "react-bootstrap";

/* Redux */
import {AnyAction} from "@reduxjs/toolkit";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../redux";
import {
  addToSelectedResults,
  removeFromSelectedResults
} from "../../../redux/slices/result-slice";

/* Components */
import {IResultsState, ISelected} from "../../../types/redux/result";
import SearchUtils from "../../../utils/search-utils";
import CardHeader from "../card-header";
import {TQueryItem} from "../../../constants";
import {NavigateFunction} from "react-router/dist/lib/hooks";
import {useNavigate} from "react-router-dom";

interface IProps  {
  searchEntry: ISearchEntry
}

const SearchCard = (props: IProps):JSX.Element => {
  const {
    searchEntry
  } = props;

  const dispatch:Dispatch<AnyAction> = useDispatch();
  const resultsState: IResultsState = useSelector<RootState, IResultsState>(state => state.result);

  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(null);
  const [localSearchState, setLocalSearchState] = useState<ISearchState | null>(null);
  const [selected, setSelected] = useState<boolean>(false);
  const navigate: NavigateFunction = useNavigate();

  useEffect(()=> {
    if (searchEntry.url) {
      let urlSearchParams: URLSearchParams;
      if (searchEntry.url.indexOf('?') === -1) {
        // my searches from solr
        urlSearchParams = SearchUtils.transposeLegacyParams(decodeURIComponent(searchEntry.url));
      } else {
        urlSearchParams = new URLSearchParams(searchEntry.url.split('?')[1]);
      }
      setSearchParams(urlSearchParams);
      setLocalSearchState(SearchUtils.fromDataSource(urlSearchParams.toString()))
    }
  }, [searchEntry.url])

  useEffect(()=>{
    /* toolbar select all */
    setSelected(resultsState.selected_ids.findIndex((select: ISelected) => String(select.id) === String(searchEntry.id)) !== -1);
  }, [ resultsState.selected_ids, searchEntry.id])

  const getLabel = useCallback((): JSX.Element | undefined => {
    if (!searchParams) return;

    let urlPath: string;
    let finalUrl: URL;

    const isLegacyUrl = !searchEntry.url.startsWith('http');

    if (isLegacyUrl) {
      const decodedUrl = decodeURIComponent(searchEntry.url);
      const legacyParams = SearchUtils.transposeLegacyParams(decodedUrl);

      const baseUrl = `${window.location.protocol}//${window.location.host}/all-industries/documents/`;
      const queryParam = legacyParams.get(Constants.QUERY_KEY);
      const filtersParam = legacyParams.get(Constants.FILTERS_KEY);

      let composedUrl = `${baseUrl}?q=${queryParam}&db-set=${Constants.DOCUMENTS}`;
      if (filtersParam) {
        composedUrl += `&f=${filtersParam}`;
      }

      finalUrl = new URL(composedUrl);
    } else {
      finalUrl = new URL(searchEntry.url);
    }

    urlPath = decodeURIComponent(finalUrl.pathname + finalUrl.search);

    const queryString = searchParams.get(Constants.QUERY_KEY);
    if (!queryString) return <></>;

    const queryItems: TQueryItem[] = SearchUtils.convertQueryParams(queryString);
    const formattedQuery = SearchUtils.convertSearchQuery(queryItems);

    return (
      <Button className="small btn-link p-0" onClick={() => navigate(urlPath)}>
        <b>{formattedQuery}</b>
      </Button>
    );
  }, [navigate, searchEntry.url, searchParams]);

  const filters = useCallback((): JSX.Element | undefined => {
    const { filters_db, filter_display } = localSearchState;

    // Early exit if prerequisites are missing
    if (!filters_db || !searchParams) return;

    const filterParamString = searchParams.get(Constants.FILTERS_KEY);
    if (!filterParamString) return <></>;

    const filterIds = filterParamString.split(',');
    const displayFilterIds = new Set(filter_display?.options.map(option => option.id));

    const excluded: string[] = [];
    const applied: string[] = [];

    for (const id of filterIds) {
      if (displayFilterIds.has(id)) {
        excluded.push(Utils.toTitleCase(id));
      } else {
        const label = Utils.toTitleCase(id.substring(id.indexOf('facet_') + 6));
        applied.push(label);
      }
    }

    localSearchState.dates.forEach(dt =>{
      applied.push(dt.to.split('/').pop());
    })

    const renderFilterSection = (title: string, labels: string[], className: string) => (
      <div className={className}>
        <p className="mb-1 text-small"><strong>{title}:</strong></p>
        <div className={'d-flex flex-row'}>
          {labels.map((label, index) => (
            <div key={index} className="pill rounded px-3 py-2 me-2 text-small">
              {label}
            </div>
          ))}
        </div>
      </div>
    );

    return (
      <div>
        {excluded.length > 0 && renderFilterSection('Excluded', excluded, 'excluded')}
        {excluded.length > 0 && applied.length > 0 && <hr />}
        {applied.length > 0 && renderFilterSection('Filters', applied, 'applied')}
      </div>
    );
  }, [localSearchState, searchParams]);

  const entry = (label: string, value: string | number | JSX.Element): JSX.Element => {
    const cssClass: string = label.toLowerCase().replace(' ', '-');
    if (value) {
      return <>
        <Col lg="2"><b>{label}</b></Col>
        <Col lg="10" className={cssClass}>{value}</Col>
      </>
    }
    return <></>
  }

  const handleSelected = (selected: boolean): void => {
    if (selected) {
      dispatch(addToSelectedResults([String(searchEntry.id)]))
    } else {
      dispatch(removeFromSelectedResults([String(searchEntry.id)]))
    }
  }

  const convertDatesToReadable = (dates: ISearchDate[]):JSX.Element =>{
    return <div className={'dates'}>
      {dates.map((searchDate: ISearchDate, index: number) => {
          return <div key={index} className={'date'}>
            <span className={'date'}>{searchDate.field}</span>
            <span className={'date-from'}>{searchDate.from}</span>
            <span>&nbsp;-&nbsp;</span>
            <span className={'date-to'}>{searchDate.to}</span>
          </div>
        }
      )}
    </div>
  }

  return ((localSearchState !== null) &&
    <RecordsContext.Provider value={{records:[searchEntry]}}>
      <Card className="search-card m-0 mb-3 p-0">
        <CardHeader
          selected={selected}
          handleSelected={handleSelected}
          getLabel={getLabel} />

        <Card.Body>
          <Tab.Container defaultActiveKey={Constants.METADATA}>
            <Nav variant="tabs" defaultActiveKey={Constants.METADATA} >
              <Nav.Item>
                <Nav.Link eventKey={Constants.METADATA}>Metadata</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={Constants.FILTERS}>Filters</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Container className={'pt-3 mx-0 px-1 pb-2'}>
                {/* Metadata TAB*/}
                <Tab.Pane eventKey={Constants.METADATA}>
                  <Row className={'doc-content small'}>
                    {entry('Search Date', new Date(searchEntry.searchDateTime).toLocaleDateString(Utils.getLocalizationStr(), {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }))}
                    {(localSearchState.dates.length > 0 ) && entry('Date Ranges', convertDatesToReadable(localSearchState.dates) )}
                    {entry('Record Count', searchEntry.count)}
                    {entry('Industry', localSearchState.industry)}
                  </Row>
                </Tab.Pane>

                {/* Filters TAB*/}
                <Tab.Pane eventKey={Constants.FILTERS}>
                  <Row className={'doc-content mt-2 '}>
                    <Col lg="12">
                      {filters()}
                    </Col>
                  </Row>
                </Tab.Pane>

              </Container>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>
    </RecordsContext.Provider>
  )
}

export default SearchCard;
